import {
  StepId,
  OnboardingContext,
  STEPS_TO_SKIP_WHEN_ASSISTED,
} from './types';
import { resolveBaseStepId } from './computeFlow';
import { differenceInYears } from 'date-fns';

export type FlowTransformer = (
  steps: StepId[],
  ctx: OnboardingContext,
) => StepId[];

const parseDate = (dateString: string | null): Date | null => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

const isUnder16 = (dateOfBirth: string | null): boolean => {
  const date = parseDate(dateOfBirth);
  if (!date) return false;
  return differenceInYears(new Date(), date) < 16;
};

const isEligibleForMonBilanPrevention = (
  dateOfBirth: string | null,
): boolean => {
  const date = parseDate(dateOfBirth);
  if (!date) return false;

  const age = differenceInYears(new Date(), date);
  const isInRange = (min: number, max: number) => age >= min && age <= max;

  return (
    isInRange(18, 25) ||
    isInRange(45, 50) ||
    isInRange(60, 65) ||
    isInRange(70, 75)
  );
};

export const skipSelfIdentificationForMinors: FlowTransformer = (
  steps,
  ctx,
) => {
  if (!ctx.patientDateOfBirth) {
    return steps;
  }

  if (isUnder16(ctx.patientDateOfBirth)) {
    return steps.filter((s) => s !== 'self-identification');
  }

  return steps;
};

/**
 * Skips self-identification when self param is explicitly set ('true' or 'false').
 * This indicates the professional has already handled identification.
 */
export const skipSelfIdentificationWhenExplicit: FlowTransformer = (
  steps,
  ctx,
) => {
  if (ctx.selfParam === 'true' || ctx.selfParam === 'false') {
    return steps.filter((s) => s !== 'self-identification');
  }

  return steps;
};

/**
 * Skips the consultation-time step for invitation mode, when no appointment is
 * scheduled, or for an assisted in-office launch whose consultation date is
 * already known. A generic walk-in (assisted but no known date) keeps the step
 * so office staff can enter the appointment date/time manually.
 */
export const skipConsultationTime: FlowTransformer = (steps, ctx) => {
  if (
    ctx.isInvitation ||
    ctx.noAppointment ||
    (ctx.assistedByPro && ctx.consultationTimeKnown)
  ) {
    return steps.filter((s) => s !== 'consultation-time');
  }

  return steps;
};

/**
 * Transforms landing-professional-select to landing-practice when
 * skipProfessionalSelection is enabled. This handles the dynamic case
 * where a practice may or may not have professional selection enabled.
 * Variant-specific landings (sommeil-bligny, etp-sommeil) are already
 * correct in BASE_FLOWS and are not transformed.
 */
export const skipProfessionalSelection: FlowTransformer = (steps, ctx) => {
  if (!ctx.skipProfessionalSelection) {
    return steps;
  }

  return steps.map((step) =>
    resolveBaseStepId(step) === 'landing-professional-select'
      ? 'landing-practice'
      : step,
  );
};

/**
 * Handles Mon Bilan Prévention ineligible patients.
 * If patient is ineligible (age > 25), inserts mbp-ineligible step after patient-info
 * and truncates the flow (remaining steps are removed).
 */
export const handleMbpIneligible: FlowTransformer = (steps, ctx) => {
  if (ctx.patientOnboarding !== 'MON_BILAN_PREVENTION') {
    return steps;
  }

  if (!ctx.patientDateOfBirth) {
    return steps;
  }

  if (!isEligibleForMonBilanPrevention(ctx.patientDateOfBirth)) {
    const patientInfoIndex = steps.findIndex(
      (s) => s === 'patient-information',
    );

    if (patientInfoIndex === -1) {
      return steps;
    }

    // Insert mbp-ineligible after patient-info and truncate everything after
    const flowUpToIneligible = steps.slice(0, patientInfoIndex + 1);
    return [...flowUpToIneligible, 'mbp-ineligible'];
  }

  return steps;
};

/**
 * Swaps experiment variant steps based on PostHog feature flags.
 * For the patient-onboarding-mar-2026 experiment, puts invitation-authentication
 * before landing-professional-select when the flag is 'test'.
 */
// export const swapExperimentVariants: FlowTransformer = (steps, ctx) => {
//   const landingFlag = ctx.experimentFlags['patient-onboarding-mar-2026'];
//
//   if (landingFlag !== 'test') {
//     return steps;
//   }
//
//   const landingIndex = steps.indexOf('landing-professional-select');
//   const authIndex = steps.indexOf('invitation-authentication');
//
//   if (landingIndex === -1 || authIndex === -1 || authIndex < landingIndex) {
//     return steps;
//   }
//
//   const newSteps = [...steps];
//   newSteps.splice(authIndex, 1);
//   newSteps.splice(landingIndex, 0, 'invitation-authentication');
//
//   return newSteps;
// };

/**
 * Removes invitation-authentication steps for non-invitation flows.
 * Only invitation flows (mode=INVITATION) should show auth steps.
 */
export const removeInvitationAuthForNonInvitation: FlowTransformer = (
  steps,
  ctx,
) => {
  if (ctx.isInvitation) {
    return steps;
  }
  return steps.filter(
    (s) => resolveBaseStepId(s) !== 'invitation-authentication',
  );
};

export const removeInvitationAuthWhenAuthenticated: FlowTransformer = (
  steps,
  ctx,
) => {
  if (!ctx.isInvitation || !ctx.isPatientAuthenticated) {
    return steps;
  }
  return steps.filter(
    (s) => resolveBaseStepId(s) !== 'invitation-authentication',
  );
};

/**
 * In-office assisted launch: a professional fills the questionnaire on the
 * patient's behalf, so skip the patient-facing onboarding (intros,
 * self-identification — declared in STEPS_TO_SKIP_WHEN_ASSISTED). The
 * doctor-selection step is dropped only when a professional is already assigned
 * (consultation-linked launch, or a professional free-start); for an assistant
 * free-start with no pre-assigned doctor it stays so the doctor can be picked
 * first. `consultation-time` is left in so staff can enter the date when needed.
 */
export const skipStepsForAssistedByPro: FlowTransformer = (steps, ctx) => {
  if (!ctx.assistedByPro) {
    return steps;
  }

  return steps.filter((step) => {
    if (STEPS_TO_SKIP_WHEN_ASSISTED.includes(step)) {
      return false;
    }
    if (
      ctx.hasAssignedProfessional &&
      (resolveBaseStepId(step) === 'landing-professional-select' ||
        resolveBaseStepId(step) === 'landing-practice')
    ) {
      return false;
    }
    return true;
  });
};

/**
 * Drops the extra patient-data pages the practice does not collect
 */
export const skipEmptyPatientDataSteps: FlowTransformer = (steps, ctx) => {
  const config = ctx.patientDataCollectionConfig;
  const stepIsCollected: Partial<Record<StepId, boolean>> = {
    'patient-administrative':
      config.BIRTH_PLACE.enabled || config.SOCIAL_SECURITY_NUMBER.enabled,
    'patient-contact': config.PHONE.enabled || config.EMAIL.enabled,
    'patient-address': config.ADDRESS.enabled,
    'patient-profession': config.PROFESSION.enabled,
  };

  return steps.filter(
    (step) => stepIsCollected[resolveBaseStepId(step)] ?? true,
  );
};

/**
 * Appends the optional catch-all document-upload page before the offboarding
 * (research-consent / confirmation) steps when the controlling professional has
 * enabled additional uploads. Skipped for truncated flows (e.g. mbp-ineligible)
 * that have no confirmation step.
 */
export const appendAdditionalDocumentUpload: FlowTransformer = (steps, ctx) => {
  if (!ctx.patientDocumentsEnabled || !ctx.allowAdditionalUploads) return steps;
  if (steps.includes('additional-document-upload')) return steps;

  const offboardingSteps: StepId[] = [
    'research-consent',
    'confirmation',
    'direct-confirmation',
  ];
  const firstOffboardingIndex = steps.findIndex((s) =>
    offboardingSteps.includes(s),
  );
  if (firstOffboardingIndex === -1) return steps;

  const next = [...steps];
  next.splice(firstOffboardingIndex, 0, 'additional-document-upload');
  return next;
};

/**
 * Ordered array of flow transformers.
 * Order matters:
 * 1. swapExperimentVariants runs FIRST (establishes correct step names)
 * 2. removeInvitationAuthForNonInvitation runs SECOND (filters auth for non-invitation)
 * 3. removeInvitationAuthWhenAuthenticated runs THIRD (filters auth for already-authenticated invitation flows)
 * 4. Independent transformers can run in any order
 * 5. handleMbpIneligible runs last (depends on patient-info step)
 *
 * skipEmptyPatientDataSteps only removes steps, so its position among the
 * independent transformers does not matter.
 *
 * Note: skipResearchConsentForSiteReferrer is NOT a transformer.
 * referrer=site offboarding skip is handled declaratively via CONFIRMATION_ONLY
 * offboarding flow in the snp-demo variant configuration.
 */
export const TRANSFORMERS: FlowTransformer[] = [
  // swapExperimentVariants,
  removeInvitationAuthForNonInvitation,
  removeInvitationAuthWhenAuthenticated,
  skipProfessionalSelection,
  skipSelfIdentificationForMinors,
  skipSelfIdentificationWhenExplicit,
  skipConsultationTime,
  skipEmptyPatientDataSteps,
  skipStepsForAssistedByPro,
  appendAdditionalDocumentUpload,
  handleMbpIneligible,
];
